import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  HardDrive,
  Database,
  Loader2,
  AlertTriangle,
  AlertCircle,
  Activity,
  Layers,
  Search,
  Download,
  Info,
  X,
  Copy,
  Check,
  TrendingUp,
  Server,
  Zap,
} from 'lucide-react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from 'recharts';
import baseapi from '../../../services/baseapi';

interface OracleStorageViewProps {
  activeCluster: string;
  timeRange: string;
  clientId?: string;
  refreshKey?: number;
  dbType?: string;
}

interface TablespaceItem {
  database: string; // tablespace name e.g. SYSTEM, SYSAUX, UNDOTBS1, TEMP, etc.
  db_type: string;
  is_system: boolean;
  size_mb: number;
  status: string;
  allocated_mb: number;
  used_mb: number;
  pct_used: number | null;
  max_size_mb: number;
  datafile_count: number;
  autoextend_count: number;
  contents: string; // PERMANENT, UNDO, TEMPORARY
  last_seen?: string;
  capacity_status: 'CRITICAL' | 'NEAR_FULL' | 'WARNING' | 'HEALTHY' | string;
  backup_flags?: any;
}

interface DatafileIOItem {
  file: string;
  tablespace: string;
  reads_per_sec: number | null;
  writes_per_sec: number | null;
  avg_read_latency_ms: number | null;
  db_type?: string;
  hostname?: string;
  collected_at?: string;
}

interface StorageSummary {
  tablespaces: number;
  fullest: string;
  fullest_pct_used: number;
  worst_capacity_status: string;
}

interface RedoUndoSummary {
  redo_bytes_per_sec: number;
  archive_log_mode: string;
  undo_blocks_in_use: number;
  log_switches_24h: number;
}

interface LogSwitchHour {
  hour_start: string;
  count: number;
}

const formatMB = (val: number | null | undefined): string => {
  if (val == null || isNaN(val)) return '0 MB';
  if (val >= 1024 * 1024) return `${(val / (1024 * 1024)).toFixed(2)} TB`;
  if (val >= 1024) return `${(val / 1024).toFixed(2)} GB`;
  return `${Number(val).toFixed(2)} MB`;
};

const formatBytesRate = (bytes: number | null | undefined): string => {
  if (bytes == null || isNaN(bytes)) return '0 B/s';
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(2)} MB/s`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(2)} KB/s`;
  return `${bytes.toFixed(1)} B/s`;
};

export const OracleStorageView: React.FC<OracleStorageViewProps> = ({
  activeCluster,
  timeRange,
  clientId,
  refreshKey,
}) => {
  const [loading, setLoading] = useState(true);
  const [storageData, setStorageData] = useState<{
    summary: StorageSummary | null;
    tablespaces: TablespaceItem[];
    datafile_io: DatafileIOItem[];
    collected_at?: string;
  }>({
    summary: null,
    tablespaces: [],
    datafile_io: [],
  });

  const [redoUndoData, setRedoUndoData] = useState<{
    summary: RedoUndoSummary | null;
    log_switches_per_hour: LogSwitchHour[];
  }>({
    summary: null,
    log_switches_per_hour: [],
  });

  const [graphData, setGraphData] = useState<{
    labels: string[];
    series: Record<string, (number | null)[]>;
    databases: string[];
  }>({
    labels: [],
    series: {},
    databases: [],
  });

  const [selectedDbGraph, setSelectedDbGraph] = useState<string>('SYSTEM');
  const [tsSearch, setTsSearch] = useState('');
  const [tsTypeFilter, setTsTypeFilter] = useState<string>('all');
  const [tsStatusFilter, setTsStatusFilter] = useState<string>('all');
  const [dfSearch, setDfSearch] = useState('');
  const [selectedTablespace, setSelectedTablespace] = useState<TablespaceItem | null>(null);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const fetchStorage = useCallback(async () => {
    if (!activeCluster) return;
    try {
      const res = await baseapi.get('/db_monitor_dashboard/oracle/storage', {
        params: { cluster_id: activeCluster, client_id: clientId },
      });
      if (res.data?.data) {
        setStorageData({
          summary: res.data.data.summary || null,
          tablespaces: res.data.data.tablespaces || [],
          datafile_io: res.data.data.datafile_io || [],
          collected_at: res.data.collected_at,
        });
      }
    } catch (err) {
      console.warn('Oracle storage fetch error:', err);
    }
  }, [activeCluster, clientId]);

  const fetchRedoUndo = useCallback(async () => {
    if (!activeCluster) return;
    try {
      const res = await baseapi.get('/db_monitor_dashboard/oracle/redo_undo_temp', {
        params: { cluster_id: activeCluster, client_id: clientId },
      });
      if (res.data?.data) {
        setRedoUndoData({
          summary: res.data.data.summary || null,
          log_switches_per_hour: res.data.data.log_switches_per_hour || [],
        });
      }
    } catch (err) {
      console.warn('Oracle redo_undo_temp fetch error:', err);
    }
  }, [activeCluster, clientId]);

  const fetchGraphData = useCallback(async () => {
    if (!activeCluster) return;
    try {
      const res = await baseapi.get('/db_monitor_dashboard/graph/per_db/commits', {
        params: {
          cluster_id: activeCluster,
          client_id: clientId,
          time_range: timeRange,
        },
      });
      if (res.data?.data) {
        setGraphData({
          labels: res.data.data.labels || [],
          series: res.data.data.series || {},
          databases: res.data.data.databases || [],
        });
        if (res.data.data.databases && res.data.data.databases.length > 0) {
          setSelectedDbGraph((prev) =>
            res.data.data.databases.includes(prev) ? prev : res.data.data.databases[0]
          );
        }
      }
    } catch (err) {
      console.warn('Oracle commits graph fetch error:', err);
    }
  }, [activeCluster, clientId, timeRange]);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchStorage(), fetchRedoUndo(), fetchGraphData()]).finally(() => {
      setLoading(false);
    });
  }, [fetchStorage, fetchRedoUndo, fetchGraphData, refreshKey]);

  // Handle copy
  const handleCopy = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 1500);
  };

  // Filtered tablespaces
  const filteredTablespaces = useMemo(() => {
    return storageData.tablespaces.filter((ts) => {
      if (tsTypeFilter !== 'all') {
        if (tsTypeFilter === 'system' && !ts.is_system) return false;
        if (tsTypeFilter === 'user' && ts.is_system) return false;
        if (['PERMANENT', 'UNDO', 'TEMPORARY'].includes(tsTypeFilter) && ts.contents !== tsTypeFilter) {
          return false;
        }
      }

      if (tsStatusFilter !== 'all' && ts.capacity_status !== tsStatusFilter) {
        return false;
      }

      if (!tsSearch.trim()) return true;
      const q = tsSearch.toLowerCase();
      return (
        ts.database.toLowerCase().includes(q) ||
        ts.contents.toLowerCase().includes(q) ||
        ts.status.toLowerCase().includes(q) ||
        ts.capacity_status.toLowerCase().includes(q)
      );
    });
  }, [storageData.tablespaces, tsTypeFilter, tsStatusFilter, tsSearch]);

  // Filtered datafiles
  const filteredDatafiles = useMemo(() => {
    if (!dfSearch.trim()) return storageData.datafile_io;
    const q = dfSearch.toLowerCase();
    return storageData.datafile_io.filter(
      (df) => df.file.toLowerCase().includes(q) || df.tablespace.toLowerCase().includes(q)
    );
  }, [storageData.datafile_io, dfSearch]);

  // Formatted log switch chart data
  const formattedLogSwitches = useMemo(() => {
    return redoUndoData.log_switches_per_hour.map((item) => {
      const d = new Date(item.hour_start);
      const hourStr = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      return {
        time: hourStr,
        fullTime: d.toLocaleString(),
        count: item.count,
      };
    });
  }, [redoUndoData.log_switches_per_hour]);

  // Growth Trend Chart Data
  const formattedGrowthTrend = useMemo(() => {
    if (!graphData.labels || graphData.labels.length === 0) return [];
    const sizeSeries = graphData.series[`${selectedDbGraph}:size_mb`] || [];
    const usedSeries = graphData.series[`${selectedDbGraph}:used_mb`] || [];

    return graphData.labels.map((lbl, idx) => {
      const d = new Date(lbl);
      const timeStr = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      return {
        time: timeStr,
        fullTime: d.toLocaleString(),
        size_mb: sizeSeries[idx] ?? null,
        used_mb: usedSeries[idx] ?? null,
      };
    });
  }, [graphData, selectedDbGraph]);

  // Severity color helper
  const getCapacityBadge = (status: string) => {
    switch (status) {
      case 'CRITICAL':
        return {
          bg: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/30',
          dot: 'bg-red-500',
          bar: 'bg-red-500',
        };
      case 'NEAR_FULL':
        return {
          bg: 'bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/30',
          dot: 'bg-orange-500',
          bar: 'bg-orange-500',
        };
      case 'WARNING':
        return {
          bg: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/30',
          dot: 'bg-amber-500',
          bar: 'bg-amber-500',
        };
      case 'HEALTHY':
      default:
        return {
          bg: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30',
          dot: 'bg-emerald-500',
          bar: 'bg-emerald-500',
        };
    }
  };

  const exportTablespacesCSV = () => {
    const headers = [
      'Tablespace',
      'Contents',
      'System TS',
      'Status',
      'Capacity Status',
      'Used MB',
      'Allocated MB',
      'Pct Used',
      'Max Size MB',
      'Datafiles',
    ];
    const rows = storageData.tablespaces.map((ts) => [
      `"${ts.database}"`,
      `"${ts.contents}"`,
      `"${ts.is_system ? 'Yes' : 'No'}"`,
      `"${ts.status}"`,
      `"${ts.capacity_status}"`,
      ts.used_mb,
      ts.allocated_mb,
      `${ts.pct_used}%`,
      ts.max_size_mb,
      ts.datafile_count,
    ]);
    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map((e) => e.join(','))].join('\n');
    const downloadAnchor = document.createElement('a');
    downloadAnchor.setAttribute('href', encodeURI(csvContent));
    downloadAnchor.setAttribute('download', `oracle_tablespaces_${activeCluster}.csv`);
    document.body.appendChild(downloadAnchor);
    downloadAnchor.click();
    downloadAnchor.remove();
  };

  if (loading) {
    return (
      <div className="bg-white/60 dark:bg-gray-900/50 backdrop-blur-sm z-10 flex items-center justify-center rounded-xl min-h-[420px]">
        <div className="flex flex-col items-center gap-2">
          <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
            Loading Oracle storage & maintenance data...
          </span>
        </div>
      </div>
    );
  }

  const summary = storageData.summary;
  const redoSummary = redoUndoData.summary;

  return (
    <div className="space-y-4">
      {/* Top Banner KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2.5">
        {/* Total Tablespaces */}
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-3 shadow-sm flex items-center gap-3">
          <div className="p-2 rounded-lg bg-orange-500/10 text-orange-600 dark:text-orange-400 border border-orange-500/20">
            <HardDrive className="w-4 h-4" />
          </div>
          <div>
            <div className="text-[10px] uppercase font-semibold tracking-wider text-gray-500 dark:text-gray-400">
              Tablespaces
            </div>
            <div className="text-base font-bold text-gray-900 dark:text-white font-mono">
              {summary?.tablespaces ?? storageData.tablespaces.length}
            </div>
          </div>
        </div>

        {/* Worst Capacity */}
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-3 shadow-sm flex items-center gap-3">
          <div
            className={`p-2 rounded-lg border ${summary?.worst_capacity_status === 'CRITICAL'
                ? 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20'
                : 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20'
              }`}
          >
            <AlertTriangle className="w-4 h-4" />
          </div>
          <div>
            <div className="text-[10px] uppercase font-semibold tracking-wider text-gray-500 dark:text-gray-400">
              Worst Status
            </div>
            <div
              className={`text-sm font-bold font-mono ${summary?.worst_capacity_status === 'CRITICAL' ? 'text-red-600 dark:text-red-400' : 'text-emerald-600'
                }`}
            >
              {summary?.worst_capacity_status || 'HEALTHY'}
            </div>
          </div>
        </div>

        {/* Fullest Tablespace */}
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-3 shadow-sm flex items-center gap-3">
          <div className="p-2 rounded-lg bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20">
            <AlertCircle className="w-4 h-4" />
          </div>
          <div className="truncate">
            <div className="text-[10px] uppercase font-semibold tracking-wider text-gray-500 dark:text-gray-400">
              Fullest Space
            </div>
            <div className="text-sm font-bold text-gray-900 dark:text-white font-mono truncate">
              {summary?.fullest || '—'}{' '}
              <span className="text-xs text-amber-500 font-semibold">
                ({summary?.fullest_pct_used ?? 0}%)
              </span>
            </div>
          </div>
        </div>

        {/* Archive Log Mode */}
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-3 shadow-sm flex items-center gap-3">
          <div className="p-2 rounded-lg bg-purple-500/10 text-purple-600 dark:text-purple-400 border border-purple-500/20">
            <Database className="w-4 h-4" />
          </div>
          <div>
            <div className="text-[10px] uppercase font-semibold tracking-wider text-gray-500 dark:text-gray-400">
              Archive Mode
            </div>
            <div className="text-xs font-bold text-purple-600 dark:text-purple-400 font-mono">
              {redoSummary?.archive_log_mode || 'NOARCHIVELOG'}
            </div>
          </div>
        </div>

        {/* Redo Generation Rate */}
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-3 shadow-sm flex items-center gap-3">
          <div className="p-2 rounded-lg bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20">
            <Zap className="w-4 h-4" />
          </div>
          <div>
            <div className="text-[10px] uppercase font-semibold tracking-wider text-gray-500 dark:text-gray-400">
              Redo Rate
            </div>
            <div className="text-sm font-bold text-gray-900 dark:text-white font-mono">
              {formatBytesRate(redoSummary?.redo_bytes_per_sec)}
            </div>
          </div>
        </div>

        {/* 24h Log Switches */}
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-3 shadow-sm flex items-center gap-3">
          <div className="p-2 rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
            <Activity className="w-4 h-4" />
          </div>
          <div>
            <div className="text-[10px] uppercase font-semibold tracking-wider text-gray-500 dark:text-gray-400">
              24h Log Switches
            </div>
            <div className="text-sm font-bold text-gray-900 dark:text-white font-mono">
              {redoSummary?.log_switches_24h ?? 0}
            </div>
          </div>
        </div>
      </div>

      {/* Main Tablespaces Inventory Table */}
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700/60 rounded-xl p-3.5 shadow-sm space-y-3">
        {/* Table Top Controls */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 border-b border-gray-200 dark:border-gray-800 pb-2.5">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-orange-500 text-white shadow-sm">
              <Layers className="w-4 h-4" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-bold text-gray-900 dark:text-white tracking-wide">
                  Oracle Tablespaces & Storage Allocation
                </h3>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-mono font-bold bg-orange-500/10 text-orange-600 dark:text-orange-400 border border-orange-500/30">
                  {filteredTablespaces.length} / {storageData.tablespaces.length}
                </span>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {/* Search Tablespace */}
            <div className="relative w-full sm:w-48">
              <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={tsSearch}
                onChange={(e) => setTsSearch(e.target.value)}
                placeholder="Search tablespace..."
                className="w-full bg-gray-50 dark:bg-gray-950/80 border border-gray-200 dark:border-gray-700/80 rounded-lg pl-8 pr-3 py-1 text-xs text-gray-900 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:border-orange-500 h-7"
              />
              {tsSearch && (
                <button
                  onClick={() => setTsSearch('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>

            {/* Type Filter */}
            <select
              value={tsTypeFilter}
              onChange={(e) => setTsTypeFilter(e.target.value)}
              className="bg-gray-50 dark:bg-gray-950/80 border border-gray-200 dark:border-gray-700/80 rounded-lg px-2 py-1 text-xs text-gray-700 dark:text-gray-300 focus:outline-none focus:border-orange-500 h-7 cursor-pointer"
            >
              <option value="all">All Contents</option>
              <option value="PERMANENT">PERMANENT</option>
              <option value="UNDO">UNDO</option>
              <option value="TEMPORARY">TEMPORARY</option>
              <option value="system">System Only</option>
              <option value="user">User Only</option>
            </select>

            {/* Status Filter */}
            <select
              value={tsStatusFilter}
              onChange={(e) => setTsStatusFilter(e.target.value)}
              className="bg-gray-50 dark:bg-gray-950/80 border border-gray-200 dark:border-gray-700/80 rounded-lg px-2 py-1 text-xs text-gray-700 dark:text-gray-300 focus:outline-none focus:border-orange-500 h-7 cursor-pointer"
            >
              <option value="all">All Statuses</option>
              <option value="CRITICAL">CRITICAL</option>
              <option value="NEAR_FULL">NEAR_FULL</option>
              <option value="WARNING">WARNING</option>
              <option value="HEALTHY">HEALTHY</option>
            </select>

            {/* Export CSV */}
            <button
              onClick={exportTablespacesCSV}
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 text-xs font-semibold h-7 cursor-pointer"
              title="Export tablespace list as CSV"
            >
              <Download className="w-3.5 h-3.5 text-orange-500" />
              <span className="hidden sm:inline">Export</span>
            </button>
          </div>
        </div>

        {/* Tablespaces Table */}
        <div className="overflow-x-auto border border-gray-200 dark:border-gray-800 rounded-lg">
          <table className="w-full text-left text-xs border-collapse">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-950 border-b border-gray-200 dark:border-gray-800 text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wider font-semibold whitespace-nowrap">
                <th className="py-2.5 px-3">Tablespace</th>
                <th className="py-2.5 px-3">Contents</th>
                <th className="py-2.5 px-3">Status</th>
                <th className="py-2.5 px-3 w-48">Storage Utilization</th>
                <th className="py-2.5 px-3">Used / Allocated</th>
                <th className="py-2.5 px-3">Max Size</th>
                <th className="py-2.5 px-3 text-center">Datafiles</th>
                <th className="py-2.5 px-3">Capacity Health</th>
                <th className="py-2.5 px-3 text-center">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800/40 text-gray-700 dark:text-gray-200">
              {filteredTablespaces.length > 0 ? (
                filteredTablespaces.map((ts) => {
                  const badge = getCapacityBadge(ts.capacity_status);

                  return (
                    <tr
                      key={ts.database}
                      onClick={() => setSelectedTablespace(ts)}
                      className="hover:bg-orange-500/5 dark:hover:bg-gray-800/40 transition-colors border-b border-gray-100 dark:border-gray-800/30 cursor-pointer group"
                    >
                      {/* Name */}
                      <td className="py-2 px-3 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-bold text-gray-900 dark:text-white text-xs">
                            {ts.database}
                          </span>
                          {ts.is_system && (
                            <span className="px-1.5 py-0.2 rounded text-[9px] font-semibold bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20">
                              SYSTEM
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Contents */}
                      <td className="py-2 px-3 whitespace-nowrap">
                        <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-950 border border-gray-200 dark:border-gray-800">
                          {ts.contents}
                        </span>
                      </td>

                      {/* Online/Offline Status */}
                      <td className="py-2 px-3 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center gap-1 px-1.5 py-0.2 rounded text-[10px] font-bold ${ts.status === 'ONLINE'
                              ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20'
                              : 'bg-red-500/10 text-red-600'
                            }`}
                        >
                          <span
                            className={`w-1.5 h-1.5 rounded-full ${ts.status === 'ONLINE' ? 'bg-emerald-500' : 'bg-red-500'
                              }`}
                          />
                          {ts.status}
                        </span>
                      </td>

                      {/* Utilization Bar */}
                      <td className="py-2 px-3">
                        <div className="space-y-1 min-w-[140px]">
                          <div className="flex justify-between items-center text-[10px] font-mono">
                            <span className="text-gray-500">
                              {ts.pct_used != null ? `${ts.pct_used.toFixed(1)}%` : '—'}
                            </span>
                            <span className="text-gray-400">
                              {formatMB(ts.used_mb)} / {formatMB(ts.allocated_mb)}
                            </span>
                          </div>
                          <div className="w-full bg-gray-100 dark:bg-gray-800 h-1.5 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all duration-300 ${badge.bar}`}
                              style={{ width: `${Math.min(100, Math.max(0, ts.pct_used ?? 0))}%` }}
                            />
                          </div>
                        </div>
                      </td>

                      {/* Used / Allocated */}
                      <td className="py-2 px-3 whitespace-nowrap font-mono text-[11px]">
                        <span className="font-semibold text-gray-900 dark:text-white">
                          {formatMB(ts.used_mb)}
                        </span>{' '}
                        <span className="text-gray-400">/ {formatMB(ts.allocated_mb)}</span>
                      </td>

                      {/* Max Size */}
                      <td className="py-2 px-3 whitespace-nowrap font-mono text-[11px] text-gray-600 dark:text-gray-400">
                        {formatMB(ts.max_size_mb)}
                        {ts.autoextend_count > 0 && (
                          <span className="ml-1 text-[9px] text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 px-1 py-0.2 rounded border border-emerald-500/20">
                            Autoextend
                          </span>
                        )}
                      </td>

                      {/* Datafile count */}
                      <td className="py-2 px-3 text-center whitespace-nowrap font-mono text-xs">
                        <span className="px-1.5 py-0.2 rounded bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 font-semibold">
                          {ts.datafile_count}
                        </span>
                      </td>

                      {/* Capacity Status */}
                      <td className="py-2 px-3 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono font-bold border ${badge.bg}`}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full ${badge.dot}`} />
                          {ts.capacity_status}
                        </span>
                      </td>

                      {/* Action */}
                      <td className="py-2 px-3 text-center">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedTablespace(ts);
                          }}
                          className="text-gray-400 hover:text-orange-500 p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800"
                        >
                          <Info className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={9} className="py-8 text-center text-gray-500 italic">
                    No tablespaces match the filter criteria.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Grid of Two Columns: Log Switches Hourly Chart & Tablespace Growth Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Hourly Log Switches Frequency Chart */}
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700/60 rounded-xl p-3.5 shadow-sm space-y-3">
          <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-800 pb-2">
            <div className="flex items-center gap-2">
              <div className="p-1 rounded bg-orange-500 text-white">
                <Activity className="w-3.5 h-3.5" />
              </div>
              <h4 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider">
                Redo Log Switches (Last 24 Hours)
              </h4>
            </div>
            <span className="text-[10px] font-mono font-bold text-orange-500 bg-orange-500/10 px-2 py-0.5 rounded border border-orange-500/20">
              Total 24h: {redoSummary?.log_switches_24h ?? 0}
            </span>
          </div>

          <div className="h-48 w-full">
            {formattedLogSwitches.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={formattedLogSwitches} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                  <XAxis
                    dataKey="time"
                    tick={{ fontSize: 9, fill: '#888888' }}
                    interval="preserveStartEnd"
                  />
                  <YAxis tick={{ fontSize: 9, fill: '#888888' }} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#111827',
                      borderColor: '#374151',
                      borderRadius: '8px',
                      fontSize: '11px',
                    }}
                    labelStyle={{ color: '#F97316', fontWeight: 'bold' }}
                    formatter={(val: any) => [`${val} switches`, 'Log Switches']}
                  />
                  <Bar dataKey="count" fill="#F97316" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-xs text-gray-400 italic">
                No log switch records available.
              </div>
            )}
          </div>
        </div>

        {/* Tablespace Growth / Size History */}
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700/60 rounded-xl p-3.5 shadow-sm space-y-3">
          <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-800 pb-2">
            <div className="flex items-center gap-2">
              <div className="p-1 rounded bg-blue-500 text-white">
                <TrendingUp className="w-3.5 h-3.5" />
              </div>
              <h4 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider">
                Tablespace Size History
              </h4>
            </div>

            {graphData.databases.length > 0 && (
              <select
                value={selectedDbGraph}
                onChange={(e) => setSelectedDbGraph(e.target.value)}
                className="bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-0.5 text-xs text-gray-800 dark:text-gray-200 font-mono focus:outline-none focus:border-blue-500 h-6 cursor-pointer"
              >
                {graphData.databases.map((db) => (
                  <option key={db} value={db}>
                    {db}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="h-48 w-full">
            {formattedGrowthTrend.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={formattedGrowthTrend} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="sizeGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#3B82F6" stopOpacity={0.0} />
                    </linearGradient>
                    <linearGradient id="usedGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10B981" stopOpacity={0.5} />
                      <stop offset="95%" stopColor="#10B981" stopOpacity={0.0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                  <XAxis
                    dataKey="time"
                    tick={{ fontSize: 9, fill: '#888888' }}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    tick={{ fontSize: 9, fill: '#888888' }}
                    tickFormatter={(val) => `${val} MB`}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#111827',
                      borderColor: '#374151',
                      borderRadius: '8px',
                      fontSize: '11px',
                    }}
                    labelStyle={{ color: '#60A5FA', fontWeight: 'bold' }}
                    formatter={(val: any, name: any) => [
                      `${val} MB`,
                      name === 'size_mb' ? 'Allocated Size' : 'Used Space',
                    ]}
                  />
                  <Area
                    type="monotone"
                    dataKey="size_mb"
                    stroke="#3B82F6"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#sizeGrad)"
                    name="size_mb"
                  />
                  <Area
                    type="monotone"
                    dataKey="used_mb"
                    stroke="#10B981"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#usedGrad)"
                    name="used_mb"
                  />
                  <Legend
                    verticalAlign="top"
                    align="right"
                    iconSize={8}
                    wrapperStyle={{ fontSize: '10px', top: -5 }}
                    formatter={(val) => (val === 'size_mb' ? 'Allocated Size' : 'Used Space')}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-xs text-gray-400 italic">
                No storage history data available for {selectedDbGraph}.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Section: Datafile I/O Activity */}
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700/60 rounded-xl p-3.5 shadow-sm space-y-3">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-gray-200 dark:border-gray-800 pb-2.5">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-emerald-500 text-white shadow-sm">
              <Server className="w-4 h-4" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-gray-900 dark:text-white tracking-wide">
                Datafile I/O Metrics & Latency
              </h3>
              <p className="text-[10px] text-gray-500">
                Disk read/write throughput and latency per physical Oracle datafile
              </p>
            </div>
          </div>

          <div className="relative w-full sm:w-56">
            <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={dfSearch}
              onChange={(e) => setDfSearch(e.target.value)}
              placeholder="Filter file or tablespace..."
              className="w-full bg-gray-50 dark:bg-gray-950/80 border border-gray-200 dark:border-gray-700/80 rounded-lg pl-8 pr-3 py-1 text-xs text-gray-900 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:border-emerald-500 h-7"
            />
            {dfSearch && (
              <button
                onClick={() => setDfSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>

        <div className="overflow-x-auto border border-gray-200 dark:border-gray-800 rounded-lg">
          <table className="w-full text-left text-xs border-collapse">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-950 border-b border-gray-200 dark:border-gray-800 text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wider font-semibold whitespace-nowrap">
                <th className="py-2 px-3">Datafile Path</th>
                <th className="py-2 px-3">Tablespace</th>
                <th className="py-2 px-3 text-right">Reads / Sec</th>
                <th className="py-2 px-3 text-right">Writes / Sec</th>
                <th className="py-2 px-3 text-right">Avg Read Latency</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800/40 text-gray-700 dark:text-gray-200">
              {filteredDatafiles.length > 0 ? (
                filteredDatafiles.map((df, idx) => {
                  const filename = df.file.split('/').pop() || df.file;
                  return (
                    <tr
                      key={idx}
                      className="hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors border-b border-gray-100 dark:border-gray-800/30 h-8"
                    >
                      {/* File Path */}
                      <td className="py-1 px-3 font-mono text-[11px] whitespace-nowrap max-w-xs truncate" title={df.file}>
                        <div className="flex items-center gap-1.5">
                          <span className="font-bold text-gray-900 dark:text-white">{filename}</span>
                          <span className="text-[10px] text-gray-400 truncate hidden sm:inline">
                            ({df.file})
                          </span>
                        </div>
                      </td>

                      {/* Tablespace */}
                      <td className="py-1 px-3 whitespace-nowrap">
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-mono font-bold bg-orange-500/10 text-orange-600 dark:text-orange-400 border border-orange-500/20">
                          {df.tablespace}
                        </span>
                      </td>

                      {/* Reads/Sec */}
                      <td className="py-1 px-3 text-right font-mono font-bold text-[11px] text-gray-900 dark:text-white">
                        {df.reads_per_sec != null ? df.reads_per_sec.toFixed(2) : '0.00'}
                      </td>

                      {/* Writes/Sec */}
                      <td className="py-1 px-3 text-right font-mono font-bold text-[11px] text-gray-900 dark:text-white">
                        {df.writes_per_sec != null ? df.writes_per_sec.toFixed(2) : '0.00'}
                      </td>

                      {/* Latency */}
                      <td className="py-1 px-3 text-right whitespace-nowrap font-mono text-[11px]">
                        <span
                          className={`px-1.5 py-0.2 rounded font-bold ${(df.avg_read_latency_ms ?? 0) > 5
                              ? 'bg-red-500/10 text-red-500'
                              : (df.avg_read_latency_ms ?? 0) > 1
                                ? 'bg-amber-500/10 text-amber-500'
                                : 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                            }`}
                        >
                          {df.avg_read_latency_ms != null ? `${df.avg_read_latency_ms.toFixed(2)} ms` : '—'}
                        </span>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={5} className="py-6 text-center text-gray-500 italic">
                    No datafile I/O records found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Tablespace Details Modal */}
      {selectedTablespace && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-2xl max-w-lg w-full overflow-hidden animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-950/50">
              <div className="flex items-center gap-2">
                <div className="p-1.5 rounded-lg bg-orange-500/10 text-orange-600 dark:text-orange-400 border border-orange-500/20">
                  <HardDrive className="w-4 h-4" />
                </div>
                <div>
                  <div className="flex items-center gap-1.5">
                    <h4 className="text-sm font-bold text-gray-900 dark:text-white font-mono">
                      {selectedTablespace.database}
                    </h4>
                    <button
                      onClick={() => handleCopy(selectedTablespace.database, 'modal-ts-name')}
                      className="text-gray-400 hover:text-orange-500 p-0.5 cursor-pointer"
                      title="Copy tablespace name"
                    >
                      {copiedKey === 'modal-ts-name' ? (
                        <Check className="w-3 h-3 text-emerald-500" />
                      ) : (
                        <Copy className="w-3 h-3" />
                      )}
                    </button>
                  </div>
                  <span className="text-[10px] text-gray-500 font-mono">
                    {selectedTablespace.contents} Tablespace • {selectedTablespace.status}
                  </span>
                </div>
              </div>
              <button
                onClick={() => setSelectedTablespace(null)}
                className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-4 space-y-4 max-h-[75vh] overflow-y-auto custom-scroll text-xs">
              {/* Utilization Summary */}
              <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 space-y-2">
                <div className="flex justify-between items-center text-xs">
                  <span className="text-gray-500">Utilization</span>
                  <span className="font-mono font-bold text-gray-900 dark:text-white">
                    {selectedTablespace.pct_used != null ? `${selectedTablespace.pct_used.toFixed(2)}%` : '—'}
                  </span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-800 h-2 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${getCapacityBadge(selectedTablespace.capacity_status).bar
                      }`}
                    style={{ width: `${Math.min(100, Math.max(0, selectedTablespace.pct_used ?? 0))}%` }}
                  />
                </div>
                <div className="flex justify-between text-[10px] font-mono text-gray-400">
                  <span>Used: {formatMB(selectedTablespace.used_mb)}</span>
                  <span>Allocated: {formatMB(selectedTablespace.allocated_mb)}</span>
                </div>
              </div>

              {/* Meta Grid */}
              <div className="grid grid-cols-2 gap-3 pt-2 text-[11px]">
                <div>
                  <span className="text-gray-500 block text-[10px]">Max Size (Limit):</span>
                  <span className="font-mono font-semibold text-gray-800 dark:text-gray-200">
                    {formatMB(selectedTablespace.max_size_mb)}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500 block text-[10px]">Datafiles:</span>
                  <span className="font-mono font-semibold text-gray-800 dark:text-gray-200">
                    {selectedTablespace.datafile_count} file(s)
                  </span>
                </div>
                <div>
                  <span className="text-gray-500 block text-[10px]">Autoextend Files:</span>
                  <span className="font-mono font-semibold text-gray-800 dark:text-gray-200">
                    {selectedTablespace.autoextend_count} autoextend file(s)
                  </span>
                </div>
                <div>
                  <span className="text-gray-500 block text-[10px]">Capacity Status:</span>
                  <span
                    className={`inline-block font-mono font-bold text-[10px] px-1.5 py-0.2 rounded border ${getCapacityBadge(selectedTablespace.capacity_status).bg
                      }`}
                  >
                    {selectedTablespace.capacity_status}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500 block text-[10px]">System Tablespace:</span>
                  <span className="font-semibold text-gray-800 dark:text-gray-200">
                    {selectedTablespace.is_system ? 'Yes' : 'No'}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500 block text-[10px]">Last Seen:</span>
                  <span className="font-mono text-gray-600 dark:text-gray-400">
                    {selectedTablespace.last_seen
                      ? new Date(selectedTablespace.last_seen).toLocaleString()
                      : '—'}
                  </span>
                </div>
              </div>

              {/* Datafiles list belonging to this tablespace */}
              <div className="pt-2 border-t border-gray-100 dark:border-gray-800">
                <span className="text-gray-500 font-semibold block text-[10px] uppercase mb-1.5">
                  Associated Datafiles
                </span>
                <div className="space-y-1.5">
                  {storageData.datafile_io.filter((d) => d.tablespace === selectedTablespace.database).length >
                    0 ? (
                    storageData.datafile_io
                      .filter((d) => d.tablespace === selectedTablespace.database)
                      .map((df, i) => (
                        <div
                          key={i}
                          className="p-2 rounded bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 text-[11px] font-mono space-y-1"
                        >
                          <div className="font-bold text-gray-900 dark:text-white break-all">{df.file}</div>
                          <div className="flex gap-4 text-[10px] text-gray-500">
                            <span>Reads: {df.reads_per_sec != null ? df.reads_per_sec.toFixed(2) : '0.00'}/s</span>
                            <span>Writes: {df.writes_per_sec != null ? df.writes_per_sec.toFixed(2) : '0.00'}/s</span>
                            <span>Latency: {df.avg_read_latency_ms != null ? `${df.avg_read_latency_ms.toFixed(2)} ms` : '—'}</span>
                          </div>
                        </div>
                      ))
                  ) : (
                    <div className="text-gray-400 italic text-[11px]">
                      No datafile performance record reported.
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-end p-3 border-t border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-950/50">
              <button
                onClick={() => setSelectedTablespace(null)}
                className="px-3 py-1.5 rounded-lg bg-gray-200 dark:bg-gray-800 hover:bg-gray-300 dark:hover:bg-gray-700 text-gray-800 dark:text-gray-200 font-semibold text-xs"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OracleStorageView;
